import React, { useState, useRef, useEffect } from 'react';
import { Send, MessageSquare, X } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Button } from '../ui/button';
import { useTranslation } from 'react-i18next';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "../ui/drawer";
import { formatCommentDate, getInitials } from '../../lib/utils';

const CommentDrawer = ({ post, isOpen, onClose, onAddComment, currentUser }) => {
  const { t } = useTranslation();
  const [comment, setComment] = useState('');
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const commentInputRef = useRef(null);
  
  useEffect(() => {
    // Check for screen size
    const checkScreenSize = () => {
      setIsSmallScreen(window.innerWidth < 640);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);
  
  useEffect(() => {
    // Focus the input when drawer opens
    if (isOpen && commentInputRef.current) {
      setTimeout(() => {
        commentInputRef.current.focus();
      }, 100);
    }
  }, [isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (comment.trim()) {
      onAddComment(post.id, comment.trim());
      setComment('');
    }
  };

  if (!post) return null;

  return (
    <Drawer open={isOpen} onOpenChange={onClose}>
      <DrawerContent className="max-h-[85vh] sm:max-h-[90vh]">
        <div className="flex flex-col h-[85vh] sm:h-[90vh]">
          {/* Enhanced header with mobile-friendly close button */}
          <DrawerHeader className="border-b border-neutral-200 dark:border-neutral-700 pb-3">
            <div className="flex items-center justify-between">
              <DrawerTitle className="flex items-center gap-2 text-base sm:text-lg pr-8">
                <MessageSquare className="h-5 w-5 text-primary" />
                <span className="truncate">
                  {t('advalvas.comments')} {t('common.on')} <span className="text-primary font-medium">{post.title}</span>
                </span>
              </DrawerTitle>
              <DrawerClose className="sm:hidden rounded-full p-1.5 hover:bg-neutral-100 dark:hover:bg-neutral-800">
                <X className="h-4 w-4" />
              </DrawerClose>
            </div>
            <DrawerDescription className="mt-1">
              {t('advalvas.commentsCount', { count: post.reactions.comments.length })}
            </DrawerDescription>
          </DrawerHeader>
          
          {/* Flexible content area that grows to fill available space */}
          <div className="flex-1 overflow-y-auto px-4 py-2">
            {post.reactions.comments.length > 0 ? (
              <div className="space-y-3 sm:space-y-4 pb-2">
                {post.reactions.comments.map(comment => (
                  <div key={comment.id} className="p-3 sm:p-4 border border-neutral-200 dark:border-neutral-700 rounded-lg">
                    <div className="flex items-start gap-2 sm:gap-3">
                      <Avatar className="h-7 w-7 sm:h-8 sm:w-8 flex-shrink-0">
                        <AvatarImage src={comment.user_avatar} />
                        <AvatarFallback>{getInitials(comment.author)}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                          <p className="font-medium truncate">{comment.author}</p>
                          <p className="text-[10px] sm:text-xs text-muted-foreground">
                            {formatCommentDate(comment.created_at)}
                          </p>
                        </div>
                        <p className="mt-1 text-sm text-neutral-700 dark:text-neutral-300 break-words">
                          {comment.text}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full py-10 sm:py-12">
                <MessageSquare className="h-10 w-10 sm:h-12 sm:w-12 text-neutral-300 dark:text-neutral-600 mb-2" />
                <p className="text-muted-foreground">{t('advalvas.noComments')}</p>
                <p className="text-sm text-muted-foreground mt-1">{t('advalvas.beFirstComment')}</p>
              </div>
            )}
          </div>

          {/* Fixed footer with improved touch targets for mobile */}
          <DrawerFooter className="border-t border-neutral-200 dark:border-neutral-700 pt-3 pb-3 sm:pb-6 px-4">
            <form onSubmit={handleSubmit} className="flex items-center gap-2 w-full">
              <Avatar className="h-7 w-7 sm:h-8 sm:w-8 flex-shrink-0">
                <AvatarImage src={currentUser?.avatar} />
                <AvatarFallback>{getInitials(currentUser?.username)}</AvatarFallback>
              </Avatar>
              <div className="flex-1 relative">
                <input
                  ref={commentInputRef}
                  type="text"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder={t('advalvas.addComment')}
                  className="w-full px-3 sm:px-4 py-2 rounded-full bg-neutral-100 dark:bg-neutral-800 
                           border border-transparent focus:border-primary focus:ring-2 
                           focus:ring-primary/20 transition-all duration-200 pr-10 text-sm"
                />
                <button 
                  type="submit" 
                  className="absolute right-1.5 sm:right-2 top-1/2 -translate-y-1/2 text-primary p-1.5 rounded-full hover:bg-primary/10 touch-manipulation"
                  disabled={!comment.trim()}
                >
                  <Send className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                </button>
              </div>
            </form>
            <DrawerClose asChild>
              <Button variant="outline" className="h-9 sm:h-10 mt-2 w-full sm:w-auto text-sm">
                {t('common.close')}
              </Button>
            </DrawerClose>
          </DrawerFooter>
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default CommentDrawer;